/**
 * Locate a cited passage inside a PDF page's text layer and return the boxes
 * that cover it, as percentages of the rendered page.
 *
 * The raster preview can only be highlighted with an estimated band, because a
 * page image carries no glyph positions — the band is derived from how far into
 * the page's text the passage sits, so it drifts on anything but uniform body
 * copy. A PDF text layer carries a transform per text run, so the same passage
 * can be boxed exactly where it is drawn.
 *
 * Kept free of pdfjs imports so the matching and geometry can be unit-tested
 * without a worker: callers pass plain text items and viewport numbers.
 */

export interface PdfTextItemLike {
  str: string
  /** Run width in PDF units, before the viewport scale. */
  width: number
  /** 6-element affine matrix positioning the run in PDF space. */
  transform: Array<number>
}

export interface PdfViewportLike {
  /** Viewport matrix mapping PDF space to device pixels. */
  transform: Array<number>
  width: number
  height: number
  scale: number
}

export interface PassageRectPct {
  leftPct: number
  topPct: number
  widthPct: number
  heightPct: number
}

interface CompactPosition {
  itemIndex: number
  charIndex: number
}

/** Same 6-element affine multiply as pdf.js `Util.transform`. */
function multiplyTransform(
  outer: Array<number>,
  inner: Array<number>,
): Array<number> {
  return [
    outer[0] * inner[0] + outer[2] * inner[1],
    outer[1] * inner[0] + outer[3] * inner[1],
    outer[0] * inner[2] + outer[2] * inner[3],
    outer[1] * inner[2] + outer[3] * inner[3],
    outer[0] * inner[4] + outer[2] * inner[5] + outer[4],
    outer[1] * inner[4] + outer[3] * inner[5] + outer[5],
  ]
}

function normalizeSnippet(snippet: string): string {
  return snippet
    .replace(/^\.\.\./, '')
    .replace(/\.\.\.$/, '')
    .replace(/\s+/g, '')
    .toLowerCase()
}

/**
 * Whitespace is dropped from both sides before matching. A text layer splits
 * a line into runs at arbitrary points, and there is no reliable way to know
 * whether two adjacent runs were separated by a space in the source — joining
 * with a space invents word breaks, joining with nothing removes real ones.
 * Comparing without whitespace at all sidesteps the guess.
 */
function compactPageText(items: Array<PdfTextItemLike>): {
  text: string
  positions: Array<CompactPosition>
} {
  let text = ''
  const positions: Array<CompactPosition> = []
  items.forEach((item, itemIndex) => {
    const raw = item.str || ''
    for (let charIndex = 0; charIndex < raw.length; charIndex += 1) {
      const char = raw[charIndex]
      if (/\s/.test(char)) continue
      text += char.toLowerCase()
      positions.push({ itemIndex, charIndex })
    }
  })
  return { text, positions }
}

function rectForItemRange(
  item: PdfTextItemLike,
  startChar: number,
  endChar: number,
  viewport: PdfViewportLike,
): PassageRectPct | null {
  const length = (item.str || '').length
  if (length <= 0 || viewport.width <= 0 || viewport.height <= 0) return null

  const tx = multiplyTransform(viewport.transform, item.transform)
  const fontHeight = Math.hypot(tx[2], tx[3])
  if (!Number.isFinite(fontHeight) || fontHeight <= 0) return null

  const runWidth = item.width * viewport.scale
  if (!Number.isFinite(runWidth) || runWidth <= 0) return null

  // Runs are short, so splitting their width evenly across characters is close
  // enough to slice a partial run without measuring individual glyphs.
  const fromFraction = Math.max(0, Math.min(1, startChar / length))
  const toFraction = Math.max(0, Math.min(1, (endChar + 1) / length))
  if (toFraction <= fromFraction) return null

  const left = tx[4] + runWidth * fromFraction
  const width = runWidth * (toFraction - fromFraction)
  // tx[5] sits on the baseline; the run's box starts a full font height above.
  const top = tx[5] - fontHeight

  return {
    leftPct: (left / viewport.width) * 100,
    topPct: (top / viewport.height) * 100,
    widthPct: (width / viewport.width) * 100,
    heightPct: (fontHeight / viewport.height) * 100,
  }
}

/** Fold boxes that sit on the same line into one, so a highlighted sentence
 * reads as a single marker pass rather than one box per text run. */
function mergeRects(rects: Array<PassageRectPct>): Array<PassageRectPct> {
  const sorted = [...rects].sort(
    (a, b) => a.topPct - b.topPct || a.leftPct - b.leftPct,
  )
  const merged: Array<PassageRectPct> = []
  for (const rect of sorted) {
    const previous = merged[merged.length - 1]
    const sameLine =
      previous != null &&
      Math.abs(previous.topPct - rect.topPct) < previous.heightPct * 0.5
    const adjacent =
      previous != null &&
      rect.leftPct <= previous.leftPct + previous.widthPct + previous.heightPct

    if (previous && sameLine && adjacent) {
      const right = Math.max(
        previous.leftPct + previous.widthPct,
        rect.leftPct + rect.widthPct,
      )
      previous.leftPct = Math.min(previous.leftPct, rect.leftPct)
      previous.widthPct = right - previous.leftPct
      previous.topPct = Math.min(previous.topPct, rect.topPct)
      previous.heightPct = Math.max(previous.heightPct, rect.heightPct)
      continue
    }
    merged.push({ ...rect })
  }
  return merged
}

export function passageRectsFromTextItems(
  items: Array<PdfTextItemLike>,
  snippet: string,
  viewport: PdfViewportLike,
): Array<PassageRectPct> {
  const needle = normalizeSnippet(snippet || '')
  if (!needle || items.length === 0) return []

  const { text, positions } = compactPageText(items)
  const start = text.indexOf(needle)
  if (start < 0) return []
  const end = start + needle.length - 1

  // Walk the matched span and cut it at run boundaries: each run contributes one
  // box covering only the characters the passage actually touches.
  const rects: Array<PassageRectPct> = []
  let runStart = positions[start]
  let previous = runStart
  for (let i = start + 1; i <= end; i += 1) {
    const position = positions[i]
    if (position.itemIndex !== previous.itemIndex) {
      const rect = rectForItemRange(
        items[runStart.itemIndex],
        runStart.charIndex,
        previous.charIndex,
        viewport,
      )
      if (rect) rects.push(rect)
      runStart = position
    }
    previous = position
  }
  const lastRect = rectForItemRange(
    items[runStart.itemIndex],
    runStart.charIndex,
    previous.charIndex,
    viewport,
  )
  if (lastRect) rects.push(lastRect)

  return mergeRects(rects)
}
